import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search, UserRound } from "lucide-react";

type PatientOption = {
  id: string;
  full_name: string;
  cpf: string | null;
  email: string | null;
};

interface PatientSelectorProps {
  patients: PatientOption[];
  selectedPatientId: string | null;
  onSelect: (patientId: string) => void;
}

export function PatientSelector({ patients, selectedPatientId, onSelect }: PatientSelectorProps) {
  const [search, setSearch] = useState("");

  const filteredPatients = useMemo(() => {
    if (!search.trim()) return patients;
    const query = search.toLowerCase();
    return patients.filter((p) => {
      const name = p.full_name.toLowerCase();
      const cpf = (p.cpf || "").replace(/\D/g, "");
      const email = (p.email || "").toLowerCase();
      return name.includes(query) || cpf.includes(query.replace(/\D/g, "")) || email.includes(query);
    });
  }, [patients, search]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Selecionar aluno/paciente</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome, CPF ou e-mail"
            className="pl-9"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <div className="grid gap-2 max-h-64 overflow-y-auto">
          {filteredPatients.map((patient) => {
            const isSelected = selectedPatientId === patient.id;
            return (
              <button
                key={patient.id}
                type="button"
                className={`text-left rounded-lg border p-3 transition-colors ${
                  isSelected ? "bg-accent border-primary" : "hover:bg-accent/50"
                }`}
                onClick={() => onSelect(patient.id)}
              >
                <p className="font-medium">{patient.full_name}</p>
                <p className="text-xs text-muted-foreground">{patient.cpf || patient.email || "Sem identificação"}</p>
              </button>
            );
          })}

          {filteredPatients.length === 0 && (
            <div className="text-sm text-muted-foreground flex items-center gap-2 py-4">
              <UserRound className="h-4 w-4" />
              Nenhum paciente encontrado
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
